import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Teacher { id: string; ad_soyad: string; }
interface Student { id: string; ogrenci_adi: string; }

interface ReportResult {
  id: string;
  title: string;
  data: Record<string, unknown>[];
  columns: { key: string; label: string }[];
}

const BRANCHES = [
  'Matematik', 'Fizik', 'Kimya', 'Biyoloji', 'Türkçe', 'Edebiyat',
  'Sosyal Bilgiler', 'Tarih', 'Coğrafya', 'Felsefe', 'İngilizce',
  'Almanca', 'Fransızca', 'Arapça', 'Din Kültürü', 'Psikoloji',
  'Rehberlik', 'YDT', 'YKS', 'TYT', 'AYT',
];

interface FilterState {
  dateFrom: string;
  dateTo: string;
  teacherId: string;
  studentId: string;
  branch: string;
  status: string;
}

const emptyFilters: FilterState = {
  dateFrom: '', dateTo: '', teacherId: '', studentId: '', branch: '', status: '',
};

const reportConfigs = [
  {
    id: 'tahsilat',
    title: 'Tahsilat Raporu',
    desc: 'Ödeme ve dekont kayıtlarını tarih aralığına ve öğretmene göre filtreleyin',
    icon: 'ri-money-dollar-circle-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    filters: ['dateFrom', 'dateTo', 'teacherId'] as (keyof FilterState)[],
  },
  {
    id: 'hakedis',
    title: 'Öğretmen Hak Ediş Raporu',
    desc: 'Öğretmen bazlı tahsilat ve hak ediş özetini görüntüleyin',
    icon: 'ri-coins-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    filters: ['dateFrom', 'dateTo', 'teacherId'] as (keyof FilterState)[],
  },
  {
    id: 'dersler',
    title: 'Öğrenci Ders Geçmişi',
    desc: 'Seçili öğrencinin tüm ders kayıtlarını ve performansını görün',
    icon: 'ri-book-open-line',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    filters: ['studentId', 'dateFrom', 'dateTo'] as (keyof FilterState)[],
  },
  {
    id: 'donusum',
    title: 'Deneme Ders Dönüşüm Raporu',
    desc: 'Deneme derslerinden aktif derse geçiş oranlarını analiz edin',
    icon: 'ri-arrow-right-up-line',
    color: 'text-green-600',
    bg: 'bg-green-50',
    filters: ['dateFrom', 'dateTo'] as (keyof FilterState)[],
  },
  {
    id: 'brans',
    title: 'Branş Bazlı Talep Raporu',
    desc: 'Hangi branşlara ne kadar talep geldiğini ve dağılımı görün',
    icon: 'ri-pie-chart-line',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    filters: ['branch'] as (keyof FilterState)[],
  },
  {
    id: 'bekleyen',
    title: 'Bekleyen Dekontlar Raporu',
    desc: 'Henüz onaylanmamış dekont ve ödemelerin listesini görün',
    icon: 'ri-file-warning-line',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    filters: ['status'] as (keyof FilterState)[],
  },
];

export default function RaporlarPage() {
  const [filters, setFilters] = useState<Record<string, FilterState>>(
    Object.fromEntries(reportConfigs.map(r => [r.id, { ...emptyFilters }]))
  );
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);

  useEffect(() => {
    const fetchMeta = async () => {
      const [{ data: t }, { data: s }] = await Promise.all([
        supabase.from('egtpanel_ogretmenler').select('id, ad_soyad').eq('aktif', true).order('ad_soyad'),
        supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi').eq('aktif', true).order('ogrenci_adi'),
      ]);
      setTeachers(t || []);
      setStudents(s || []);
    };
    fetchMeta();
  }, []);

  const setFilter = (reportId: string, key: keyof FilterState, val: string) => {
    setFilters(prev => ({ ...prev, [reportId]: { ...prev[reportId], [key]: val } }));
  };

  const runReport = async (reportId: string) => {
    const f = filters[reportId];
    setLoading(true);
    let data: Record<string, unknown>[] = [];
    let columns: { key: string; label: string }[] = [];

    try {
      switch (reportId) {
        case 'tahsilat': {
          let q = supabase.from('egtpanel_odemeler').select('id, dekont_tarihi, tutar, odeme_durumu, yonetici_onay, ogrenci:egtpanel_ogrenciler(ogrenci_adi), ogretmen:egtpanel_ogretmenler(ad_soyad)').order('dekont_tarihi', { ascending: false });
          if (f.dateFrom) q = q.gte('dekont_tarihi', f.dateFrom);
          if (f.dateTo) q = q.lte('dekont_tarihi', f.dateTo);
          if (f.teacherId) q = q.eq('ogretmen_id', f.teacherId);
          const { data: rows } = await q;
          data = (rows || []).map((r: Record<string, unknown>) => ({
            ...r,
            ogrenci_adi: (r.ogrenci as { ogrenci_adi?: string })?.ogrenci_adi ?? '—',
            ogretmen_adi: (r.ogretmen as { ad_soyad?: string })?.ad_soyad ?? '—',
            tutar: `₺${Number(r.tutar || 0).toLocaleString('tr-TR')}`,
            durum: r.yonetici_onay ? 'Onaylandı' : (r.odeme_durumu ?? '—'),
          }));
          columns = [
            { key: 'dekont_tarihi', label: 'Tarih' },
            { key: 'ogrenci_adi', label: 'Öğrenci' },
            { key: 'ogretmen_adi', label: 'Öğretmen' },
            { key: 'tutar', label: 'Tutar' },
            { key: 'durum', label: 'Durum' },
          ];
          break;
        }
        case 'hakedis': {
          let tQ = supabase.from('egtpanel_ogretmenler').select('id, ad_soyad, hakedis_orani').eq('aktif', true);
          if (f.teacherId) tQ = tQ.eq('id', f.teacherId);
          const { data: trows } = await tQ;
          const enriched = await Promise.all((trows || []).map(async (tr: Record<string, unknown>) => {
            let pQ = supabase.from('egtpanel_odemeler').select('tutar, ogretmen_payi').eq('ogretmen_id', tr.id).eq('yonetici_onay', true);
            if (f.dateFrom) pQ = pQ.gte('dekont_tarihi', f.dateFrom);
            if (f.dateTo) pQ = pQ.lte('dekont_tarihi', f.dateTo);
            const { data: prow } = await pQ;
            const toplam = (prow || []).reduce((a, p: Record<string, unknown>) => a + Number(p.tutar || 0), 0);
            const hakedis = (prow || []).reduce((a, p: Record<string, unknown>) => a + Number(p.ogretmen_payi || Math.round(Number(p.tutar || 0) * (Number(tr.hakedis_orani || 50) / 100))), 0);
            return { ogretmen: tr.ad_soyad, oran: `%${tr.hakedis_orani || 50}`, toplam_tahsilat: `₺${toplam.toLocaleString('tr-TR')}`, hak_edis: `₺${hakedis.toLocaleString('tr-TR')}` };
          }));
          data = enriched;
          columns = [
            { key: 'ogretmen', label: 'Öğretmen' },
            { key: 'oran', label: 'Oran' },
            { key: 'toplam_tahsilat', label: 'Toplam Tahsilat' },
            { key: 'hak_edis', label: 'Hak Ediş' },
          ];
          break;
        }
        case 'dersler': {
          if (!f.studentId) {
            data = [];
            columns = [{ key: 'bilgi', label: 'Lütfen öğrenci seçin' }];
            break;
          }
          let q = supabase.from('egtpanel_aktif_dersler').select('id, ders_tarihi, brans, islenen_konu, ders_suresi, ders_durumu, odev_verildi, ogretmen:egtpanel_ogretmenler(ad_soyad)').eq('ogrenci_id', f.studentId).order('ders_tarihi', { ascending: false });
          if (f.dateFrom) q = q.gte('ders_tarihi', f.dateFrom);
          if (f.dateTo) q = q.lte('ders_tarihi', f.dateTo);
          const { data: rows } = await q;
          data = (rows || []).map((r: Record<string, unknown>) => ({
            ...r,
            ogretmen: (r.ogretmen as { ad_soyad?: string })?.ad_soyad ?? '—',
            odev: r.odev_verildi ? 'Verildi' : 'Yok',
          }));
          columns = [
            { key: 'ders_tarihi', label: 'Tarih' },
            { key: 'brans', label: 'Branş' },
            { key: 'islenen_konu', label: 'Konu' },
            { key: 'ders_suresi', label: 'Süre (dk)' },
            { key: 'ders_durumu', label: 'Durum' },
            { key: 'odev', label: 'Ödev' },
          ];
          break;
        }
        case 'donusum': {
          let q = supabase.from('egtpanel_deneme_dersleri').select('sonuc, durum');
          if (f.dateFrom) q = q.gte('planlanan_tarih', f.dateFrom);
          if (f.dateTo) q = q.lte('planlanan_tarih', f.dateTo);
          const { data: rows } = await q;
          const counts: Record<string, number> = {};
          (rows || []).forEach((r: Record<string, unknown>) => {
            const key = (r.sonuc as string) || 'Sonuç Belirlenmedi';
            counts[key] = (counts[key] || 0) + 1;
          });
          data = Object.entries(counts).map(([name, value]) => ({ durum: name, adet: value }));
          columns = [
            { key: 'durum', label: 'Sonuç' },
            { key: 'adet', label: 'Adet' },
          ];
          break;
        }
        case 'brans': {
          const [{ data: appB }, { data: stuB }] = await Promise.all([
            supabase.from('egtpanel_basvurular').select('brans'),
            supabase.from('egtpanel_ogrenciler').select('branslar'),
          ]);
          const counts: Record<string, { basvuru: number; ogrenci: number }> = {};
          (appB || []).forEach((r: Record<string, unknown>) => {
            const b = (r as { brans?: string }).brans;
            if (b) {
              if (!counts[b]) counts[b] = { basvuru: 0, ogrenci: 0 };
              counts[b].basvuru++;
            }
          });
          (stuB || []).forEach((r: Record<string, unknown>) => {
            const arr = (r as { branslar?: string[] }).branslar;
            if (Array.isArray(arr)) arr.forEach(b => {
              if (!counts[b]) counts[b] = { basvuru: 0, ogrenci: 0 };
              counts[b].ogrenci++;
            });
          });
          const filtered = f.branch ? { [f.branch]: counts[f.branch] || { basvuru: 0, ogrenci: 0 } } : counts;
          data = Object.entries(filtered).map(([brans, v]) => ({ brans, basvuru: v.basvuru, ogrenci: v.ogrenci, toplam: v.basvuru + v.ogrenci }));
          data.sort((a, b) => (b.toplam as number) - (a.toplam as number));
          columns = [
            { key: 'brans', label: 'Branş' },
            { key: 'basvuru', label: 'Başvuru' },
            { key: 'ogrenci', label: 'Aktif Öğrenci' },
            { key: 'toplam', label: 'Toplam Talep' },
          ];
          break;
        }
        case 'bekleyen': {
          let q = supabase.from('egtpanel_odemeler').select('id, dekont_tarihi, tutar, odeme_durumu, ogrenci:egtpanel_ogrenciler(ogrenci_adi), ogretmen:egtpanel_ogretmenler(ad_soyad)').eq('yonetici_onay', false).neq('odeme_durumu', 'İptal').order('olusturma_tarihi', { ascending: false });
          if (f.status) q = q.eq('odeme_durumu', f.status);
          const { data: rows } = await q;
          data = (rows || []).map((r: Record<string, unknown>) => ({
            ...r,
            ogrenci_adi: (r.ogrenci as { ogrenci_adi?: string })?.ogrenci_adi ?? '—',
            ogretmen_adi: (r.ogretmen as { ad_soyad?: string })?.ad_soyad ?? '—',
            tutar: `₺${Number(r.tutar || 0).toLocaleString('tr-TR')}`,
          }));
          columns = [
            { key: 'dekont_tarihi', label: 'Tarih' },
            { key: 'ogrenci_adi', label: 'Öğrenci' },
            { key: 'ogretmen_adi', label: 'Öğretmen' },
            { key: 'tutar', label: 'Tutar' },
            { key: 'odeme_durumu', label: 'Durum' },
          ];
          break;
        }
      }
    } catch (e) {
      console.error('Rapor hatası:', e);
    }

    setResult({ id: reportId, title: reportConfigs.find(r => r.id === reportId)?.title || '', data, columns });
    setLoading(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Raporlar</h1>
        <p className="text-slate-500 text-sm mt-1">İstediğiniz raporu filtreleyin ve sonuçlarını görüntüleyin</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {reportConfigs.map(r => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 p-5 space-y-4">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${r.bg} shrink-0`}>
                <i className={`${r.icon} ${r.color} text-xl`} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 text-sm">{r.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{r.desc}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {r.filters.includes('dateFrom') && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Başlangıç</label>
                  <input type="date" value={filters[r.id].dateFrom} onChange={e => setFilter(r.id, 'dateFrom', e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
              )}
              {r.filters.includes('dateTo') && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Bitiş</label>
                  <input type="date" value={filters[r.id].dateTo} onChange={e => setFilter(r.id, 'dateTo', e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
              )}
              {r.filters.includes('teacherId') && (
                <div className={r.filters.length === 1 ? 'col-span-2' : ''}>
                  <label className="block text-xs text-slate-500 mb-1">Öğretmen</label>
                  <select value={filters[r.id].teacherId} onChange={e => setFilter(r.id, 'teacherId', e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Tümü</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}
                  </select>
                </div>
              )}
              {r.filters.includes('studentId') && (
                <div className={r.filters.length === 1 ? 'col-span-2' : ''}>
                  <label className="block text-xs text-slate-500 mb-1">Öğrenci</label>
                  <select value={filters[r.id].studentId} onChange={e => setFilter(r.id, 'studentId', e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.ogrenci_adi}</option>)}
                  </select>
                </div>
              )}
              {r.filters.includes('branch') && (
                <div className={r.filters.length === 1 ? 'col-span-2' : ''}>
                  <label className="block text-xs text-slate-500 mb-1">Branş</label>
                  <select value={filters[r.id].branch} onChange={e => setFilter(r.id, 'branch', e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Tümü</option>
                    {BRANCHES.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
              )}
              {r.filters.includes('status') && (
                <div className={r.filters.length === 1 ? 'col-span-2' : ''}>
                  <label className="block text-xs text-slate-500 mb-1">Durum</label>
                  <select value={filters[r.id].status} onChange={e => setFilter(r.id, 'status', e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Tümü</option>
                    <option>Dekont Bekleniyor</option>
                    <option>Dekont Geldi</option>
                    <option>Yönetici Kontrolünde</option>
                  </select>
                </div>
              )}
            </div>

            <button
              onClick={() => runReport(r.id)}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-bar-chart-2-line" />
              {loading ? 'Yükleniyor...' : 'Raporu Görüntüle'}
            </button>
          </div>
        ))}
      </div>

      {/* Result Modal */}
      {result && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">{result.title}</h2>
              <button onClick={() => setResult(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {result.data.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-file-list-3-line text-4xl text-slate-200 block mb-3" />
                  <p className="text-slate-500">Bu filtrelere uygun kayıt bulunamadı</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        {result.columns.map(c => (
                          <th key={c.key} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {result.data.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                          {result.columns.map(c => (
                            <td key={c.key} className="px-4 py-3 whitespace-nowrap text-slate-700">{String(row[c.key] ?? '—')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}